import { ThemeProvider } from '@mui/material/styles';
import useCourseTheme from '../../core/hooks/useCourseTheme';
import AppBar from '@mui/material/AppBar';
import Hidden from '@mui/material/Hidden';
import Toolbar from '@mui/material/Toolbar';
import clsx from 'clsx';
import NavbarToggleButton from './NavbarToggleButton';
import { toolbarTheme } from '../styles/defaultTheme';
import UserMenu from './UserMenu';
import NotificationsMenu from './NotificationsMenu';
import PlatformTutorial from './PlatformTutorial';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@mui/material';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ScienceIcon from '@mui/icons-material/Science';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import useNewNavigation from '../../core/hooks/useNewNavigation';
import useThemeMediaQuery from '../../core/hooks/useThemeMediaQuery';
import { useNavigationCore } from '../../core/router/navigation';
import { INICIO } from '../../core/router/routes';
import useCourseSections, { CourseSection } from './useCourseSections';
import CourseSelector from './CourseSelector';

const ACCENT = '#4F46E5';

interface Props {
  className?: string;
}

const ToolbarLayout = (props: Props) => {
  const { pathname } = useLocation();
  const { goToInicio } = useNavigationCore();
  const { enabled: newNavigation, hideNavbar } = useNewNavigation();
  const isPhone = useThemeMediaQuery((theme) => theme.breakpoints.down('md'));
  const { course, sections, tools } = useCourseSections();

  // En el celular, con la navegación nueva, las secciones del curso van en las pestañas de abajo
  // (también para los administradores, que además conservan su menú lateral)
  const showSectionsHere = !(newNavigation && isPhone);
  // En un curso con color propio la pestaña activa toma ese color
  const accent = useCourseTheme()?.main ?? ACCENT;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClose = () => {
    setAnchorEl(null);
  };

  // La pestaña activa se marca solo con la navegación nueva (antes no había marca)
  const activeSx = (active: boolean) => (newNavigation && active
    ? { color: accent, borderRadius: 0, boxShadow: `inset 0 -2px 0 ${accent}` }
    : {});

  const sectionButton = (section: CourseSection) => (
    <Button
      key={section.key}
      data-tour={section.tour}
      className="h-40 mx-1 md:mx-2 min-w-40"
      onClick={section.onClick}
      size="large"
      sx={activeSx(section.path.test(pathname))}
    >
      <span className="inline-flex mr-0 md:mr-2">{section.icon}</span>
      <span className="hidden md:block">{section.label}</span>
    </Button>
  );

  const beforeTools = sections.filter((section) => section.key !== 'alliances');
  const alliances = sections.find((section) => section.key === 'alliances');

  return (
    <ThemeProvider theme={toolbarTheme}>
      <AppBar
        id="fuse-toolbar"
        className={clsx('flex shadow-md', props.className)}
        color="default"
        sx={{
          backgroundColor: toolbarTheme.palette.background.paper,
          zIndex: (theme) => theme.zIndex.appBar,
          paddingTop: 'env(safe-area-inset-top, 0px)',
          touchAction: 'manipulation',
        }}
        position="sticky"
      >
        <Toolbar className="p-0 min-h-48 md:min-h-64 w-full" sx={{ touchAction: 'manipulation' }}>
          <div className="flex items-center shrink-0 px-8 sm:px-16">
            {hideNavbar ? (
              <img
                src={`${process.env.PUBLIC_URL}/assets/images/logo/inversapiens-negro.png`}
                alt="Inversapiens"
                style={{ height: isPhone ? 20 : 24, cursor: 'pointer' }}
                onClick={goToInicio}
              />
            ) : (
              <Hidden lgUp>
                <span data-tour="menu" className="inline-flex">
                  <NavbarToggleButton className="w-40 h-40 p-0 mx-0 sm:mx-8" />
                </span>
              </Hidden>
            )}
          </div>

          <div className="flex flex-1 min-w-0 items-center h-full overflow-x-auto px-4">
            {newNavigation && !isPhone && (
              <Button className="h-40 mx-1 md:mx-2 min-w-40" onClick={goToInicio} size="large" sx={activeSx(pathname === INICIO)}>
                <HomeRoundedIcon className="mr-0 md:mr-2" />
                <span className="hidden md:block">Inicio</span>
              </Button>
            )}
            {newNavigation && <CourseSelector compact={isPhone} />}
            {newNavigation && course && showSectionsHere && (
              <span className="mx-4 h-24 shrink-0" style={{ width: 1, background: '#E2E8F0' }} />
            )}

            {showSectionsHere && beforeTools.map(sectionButton)}
            {showSectionsHere && tools.length > 0 && (
              <>
                <Button
                  data-tour="tools"
                  className="h-40 mx-1 md:mx-2 min-w-40"
                  onClick={(event) => setAnchorEl(event.currentTarget)}
                  size="large"
                  aria-controls={open ? 'diagnosticador-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? 'true' : undefined}
                >
                  <ScienceIcon className="mr-0 md:mr-2" />
                  <span className="hidden md:block">Herramientas</span>
                </Button>
                <Menu
                  id="diagnosticador-menu"
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleClose}
                  MenuListProps={{ 'aria-labelledby': 'diagnosticador-button' }}
                >
                  {tools.map((tool) => (
                    <MenuItem key={tool.label} onClick={() => { tool.onClick(); handleClose(); }}>
                      {tool.label}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}
            {showSectionsHere && alliances && sectionButton(alliances)}
          </div>

          <div className="flex items-center shrink-0 px-4 sm:px-8 h-full">
            <PlatformTutorial iconOnly={newNavigation} />

            <span data-tour="notifications" className="inline-flex">
              <NotificationsMenu />
            </span>

            <span data-tour="profile" className="inline-flex">
              <UserMenu />
            </span>
          </div>
        </Toolbar>
      </AppBar>
    </ThemeProvider >
  );
};

export default ToolbarLayout;
